/**
 * BreadcrumbContext
 *
 * Allows pages to set custom labels for breadcrumb path segments.
 * Useful for replacing IDs with human-readable names.
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from "react";

const BreadcrumbContext = createContext({
  overrides: {},
  setOverride: () => {},
  clearOverride: () => {},
});

export const BreadcrumbProvider = ({ children }) => {
  const [overrides, setOverrides] = useState({});

  // Set a custom label for a path segment
  // e.g., setOverride("abc123", "My Mailshot Name")
  const setOverride = useCallback((segment, label) => {
    setOverrides((prev) => ({
      ...prev,
      [segment]: label,
    }));
  }, []);

  // Clear an override
  const clearOverride = useCallback((segment) => {
    setOverrides((prev) => {
      const updated = { ...prev };
      delete updated[segment];
      return updated;
    });
  }, []);

  // Memoize so consumers get a stable reference (callbacks are already stable).
  const value = useMemo(
    () => ({ overrides, setOverride, clearOverride }),
    [overrides, setOverride, clearOverride]
  );

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

export const useBreadcrumb = () => useContext(BreadcrumbContext);

export default BreadcrumbContext;
